import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, Col, Form, Row, Table } from '@edgebox/react-components';
import { useNavigate } from 'react-router-dom';
import { IPagedListResponse } from '@edgebox/data-definition-kit';
import { AccessScope, AiProviderStatus } from '@edgebox/sync-core-data-definitions';
import { ClientAiProviderEntity } from '@edgebox/sync-core-rest-client';
import { AccessScopeFilter, enumValues, normalizeFilterId, StatusFilter, useSyncCoreApi } from '../aiHelpers';
import { ErrorCard } from '../components/ErrorCard';
import { LoadingCard } from '../components/LoadingCard';
import { ScreenHeader } from '../components/ScreenHeader';

export function AiProvidersView() {
  const api = useSyncCoreApi();
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<StatusFilter<AiProviderStatus>>('Default');
  const [accessScope, setAccessScope] = useState<AccessScopeFilter>('Any');
  const [customerId, setCustomerId] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [response, setResponse] = useState<IPagedListResponse<ClientAiProviderEntity> | undefined>(undefined);

  const filters = useMemo(() => {
    return {
      status: statusFilter === 'Default' ? [AiProviderStatus.Active] : statusFilter,
      accessScope: accessScope === 'Any' ? undefined : accessScope,
      customerId: normalizeFilterId(customerId),
      projectId: normalizeFilterId(projectId),
    };
  }, [statusFilter, accessScope, customerId, projectId]);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api.ai.providers
      .list({ page: 0, itemsPerPage: 50 }, filters)
      .then((res) => {
        if (cancelled) return;
        setResponse(res);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, filters]);

  const items = response?.items ?? [];

  return (
    <>
      <ScreenHeader
        title="Providers"
        right={
          <Button variant="primary" onClick={() => navigate('/ai/provider/new')}>
            Add provider
          </Button>
        }
      />

      <Card className="mb-3">
        <CardBody>
          <Form>
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={statusFilter === 'Default' ? 'Default' : 'Custom'}
                    onChange={(e) => setStatusFilter(e.target.value === 'Default' ? 'Default' : [])}
                  >
                    <option value="Default">Default (Active)</option>
                    <option value="Custom">Custom</option>
                  </Form.Select>
                </Form.Group>

                {statusFilter !== 'Default' && (
                  <div className="mt-2">
                    {enumValues(AiProviderStatus).map((s) => (
                      <Form.Check
                        key={s}
                        type="checkbox"
                        label={s}
                        checked={(statusFilter as AiProviderStatus[]).includes(s)}
                        onChange={(e) => {
                          const current = statusFilter as AiProviderStatus[];
                          setStatusFilter(e.target.checked ? [...current, s] : current.filter((x) => x !== s));
                        }}
                      />
                    ))}
                  </div>
                )}
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Access scope</Form.Label>
                  <Form.Select value={accessScope} onChange={(e) => setAccessScope(e.target.value as AccessScopeFilter)}>
                    <option value="Any">Any</option>
                    {enumValues(AccessScope).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Customer ID</Form.Label>
                  <Form.Control value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Project ID</Form.Label>
                  <Form.Control value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      {loading ? <LoadingCard text="Loading providers…" /> : null}
      {error ? <ErrorCard error={error} /> : null}

      <Card>
        <CardHeader>
          Providers <Badge bg="secondary">{response?.totalNumberOfItems ?? items.length}</Badge>
        </CardHeader>
        <CardBody>
          <Table striped bordered hover size="sm" responsive className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Scope</th>
                <th>Frozen</th>
                <th>Type</th>
                <th>Region</th>
                <th>Customer</th>
                <th>Project</th>
                <th style={{ width: '1%' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>
                    <Badge bg={p.status === AiProviderStatus.Active ? 'success' : 'secondary'}>{p.status}</Badge>
                  </td>
                  <td>{p.accessScope}</td>
                  <td>{p.frozen ? 'Yes' : 'No'}</td>
                  <td>{p.apiType ?? '-'}</td>
                  <td>{p.region ?? '-'}</td>
                  <td>{p.customer?.getId?.() ?? '-'}</td>
                  <td>{p.project?.getId?.() ?? '-'}</td>
                  <td>
                    <Button size="sm" variant="light" onClick={() => navigate(`/ai/provider/${p.id}`)}>
                      Details
                    </Button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={9} className="text-muted">
                    No providers found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
}
